import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

import SearchIcon from "@mui/icons-material/Search";

import styles from "./dashboardNav.module.scss";

import GitHubIcon from "@mui/icons-material/GitHub";

import Brightness4OutlinedIcon from "@mui/icons-material/Brightness4Outlined";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import CodeIcon from "@mui/icons-material/Code";
import * as ComponentsDB from "../../../constants/db";
import { useActivityUtils } from "../../../utils/activityUtils";

type Props = {
  pageName: string;
  onSearch?: (searchTerm: string) => void;
};

interface SearchResult {
  type: "component" | "file" | "collection";
  name: string;
  location: string;
  description: string;
}

export default function DashboardNav({ pageName, onSearch }: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const { logCreateFolder } = useActivityUtils();

  // Get all built-in components from the database
  const getAllComponents = (): SearchResult[] => {
    const components: SearchResult[] = [];

    // Add Layout Components
    const layoutComponents = [
      "ImageCarousels",
      "Charts",
      "SearchFilters",
      "MapIntegration",
      "Popover",
      "FileUpload",
      "SocialShare",
      "Ratings",
      "FeedbackForm",
      "ErrorHandler",
      "ContainerComponents",
      "NavigationBars",
      "Sidebar",
      "PageTemplates",
      "FluidLayouts",
      "TabbedInterfaces",
      "ResponsiveImages",
      "ResponsiveTypography",
      "CollapsiblePanels",
      "GridSystems",
    ];

    layoutComponents.forEach((componentName) => {
      const component =
        ComponentsDB[componentName as keyof typeof ComponentsDB];
      if (component) {
        components.push({
          type: "component",
          name: componentName,
          location: "Layout Components",
          description: component.description,
        });
      }
    });

    // Add Form Components
    const formComponents = [
      "Input",
      "Button",
      "Checkbox",
      "RadioButton",
      "Rating",
      "Select",
      "Slider",
      "Switch",
      "DropDown",
      "ToggleButton",
    ];

    formComponents.forEach((componentName) => {
      const component =
        ComponentsDB[componentName as keyof typeof ComponentsDB];
      if (component) {
        components.push({
          type: "component",
          name: componentName,
          location: "Form Components",
          description: component.description,
        });
      }
    });

    // Add Feedback Components
    const feedbackComponents = [
      "Alert",
      "ProgressIndicator",
      "BadgeNotification",
      "SnackbarNotification",
      "Spinner",
      "ToastNotification",
      "GeoLocationFeedback",
      "UserFeedbackWidget",
      "DragAndDrop",
    ];

    feedbackComponents.forEach((componentName) => {
      const component =
        ComponentsDB[componentName as keyof typeof ComponentsDB];
      if (component) {
        components.push({
          type: "component",
          name: componentName,
          location: "Feedback Components",
          description: component.description,
        });
      }
    });

    // Add Utility Components
    const utilityComponents = [
      "CachingMechanisms",
      "FileHandling",
      "AuthenticationUtilities",
      "EncryptionUtilities",
      "EmailHandling",
      "DataCompression",
      "DependencyInjection",
      "UtilitiesLibrary",
      "Statistics",
      "SearchAndIndexing",
    ];

    utilityComponents.forEach((componentName) => {
      const component =
        ComponentsDB[componentName as keyof typeof ComponentsDB];
      if (component) {
        components.push({
          type: "component",
          name: componentName,
          location: "Utility Components",
          description: component.description,
        });
      }
    });

    // Add Data Display Components
    const dataDisplayComponents = [
      "Badge",
      "Lists",
      "Divider",
      "Typography",
      "Tables",
      "DataGrids",
      "DataComparisons",
      "ChartsAndGraphs",
    ];

    dataDisplayComponents.forEach((componentName) => {
      const component =
        ComponentsDB[componentName as keyof typeof ComponentsDB];
      if (component) {
        components.push({
          type: "component",
          name: componentName,
          location: "Data Display Components",
          description: component.description,
        });
      }
    });

    return components;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim()) {
      // Get all components and filter based on search term
      const allComponents = getAllComponents();
      const filtered = allComponents.filter(
        (result) =>
          result.name.toLowerCase().includes(value.toLowerCase()) ||
          result.location.toLowerCase().includes(value.toLowerCase()) ||
          result.description.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(filtered);
      setShowSearchModal(true);
    } else {
      setShowSearchModal(false);
      setSearchResults([]);
    }

    if (onSearch) {
      onSearch(value);
    }
  };

  const handleCloseModal = () => {
    setShowSearchModal(false);
    setSearchTerm("");
    setSearchResults([]);
  };

  const handleNewFolderClick = () => {
    // Log activity when "New Folder" is clicked
    logCreateFolder("New Folder");
    // You can add additional logic here for creating folders
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "component":
        return <CodeIcon fontSize="small" />;
      case "file":
        return <FolderIcon fontSize="small" />;
      case "collection":
        return <FolderIcon fontSize="small" />;
      default:
        return <CodeIcon fontSize="small" />;
    }
  };

  return (
    <>
      <nav className={styles.dashboard_navigation}>
        {/* Logo */}
        <div className="navigation__logo">
          <Link to="/">
            <p> PLAYBOX </p>
          </Link>
        </div>

        {/* contents */}
        <div className={styles.dashboard__contents}>
          {/* first */}
          <div className={styles.page_section}>
            <p> {pageName} </p>
            <div
              className={styles.create_folder}
              onClick={handleNewFolderClick}
            >
              <span> + New Folder </span>
            </div>
          </div>
          {/* second */}
          <div className={styles.input_section}>
            <div className={styles.searchbar_wrapper}>
              <input
                type="text"
                placeholder="Search components..."
                className={styles.searchbar}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <SearchIcon fontSize="medium" className={styles.searchbar_icon} />
            </div>
            {/* social media */}
            <div className={styles.socialMedia_container}>
              <GitHubIcon
                fontSize="large"
                className={styles.socialMedia_icon}
              />
            </div>
            <div className={styles.socialMedia_container}>
              <Brightness4OutlinedIcon
                fontSize="large"
                className={styles.socialMedia_icon}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Search Modal */}
      {showSearchModal && (
        <div className={styles.searchModal_overlay}>
          <div
            className={styles.searchModal_bg}
            onClick={handleCloseModal}
          ></div>
          <div className={styles.searchModal}>
            <div className={styles.searchModal_header}>
              <h3>Search Results for "{searchTerm}"</h3>
              <CloseIcon
                className={styles.searchModal_close}
                onClick={handleCloseModal}
              />
            </div>

            <div className={styles.searchModal_content}>
              {searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <div key={index} className={styles.searchResult_item}>
                    <div className={styles.searchResult_icon}>
                      {getIconForType(result.type)}
                    </div>
                    <div className={styles.searchResult_info}>
                      <h4>{result.name}</h4>
                      <p className={styles.searchResult_location}>
                        📁 {result.location}
                      </p>
                      <p className={styles.searchResult_description}>
                        {result.description}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.searchResult_empty}>
                  <p>No results found for "{searchTerm}"</p>
                  <p>Try searching for different keywords</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
